import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { 
  Users, DollarSign, Clock, Shield, TrendingUp, 
  CheckCircle, FileText, UserCheck, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/dashboard/stats`, { 
        withCredentials: true, 
        headers 
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">
              Admin Control Panel
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage deals, investors, and platform operations
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="role-badge bg-red-500/10 text-red-500 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Administrator
            </span>
          </div>
        </div>

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="stat-card" data-testid="stat-pending-review">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Pending Review
              </CardTitle>
              <Clock className="w-4 h-4 text-orange-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-orange-500">
                {loading ? "-" : stats?.pending_review || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-live-deals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Live Deals
              </CardTitle>
              <TrendingUp className="w-4 h-4 text-green-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-green-500">
                {loading ? "-" : stats?.live_deals || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-under-contract">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Under Contract
              </CardTitle>
              <FileText className="w-4 h-4 text-blue-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-blue-500">
                {loading ? "-" : stats?.under_contract || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-closed-deals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Deals Closed
              </CardTitle>
              <CheckCircle className="w-4 h-4 text-purple-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold">
                {loading ? "-" : stats?.closed_deals || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-total-investors">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Total Investors
              </CardTitle>
              <UserCheck className="w-4 h-4 text-indigo-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold">
                {loading ? "-" : stats?.total_investors || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-total-users">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Total Users
              </CardTitle>
              <Users className="w-4 h-4 text-gray-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold">
                {loading ? "-" : stats?.total_users || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/dashboard/admin/live-deals" className="block">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-medium">Live Deals</h3>
                      <p className="text-sm text-muted-foreground">Manage & assign deals</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/admin/investors" className="block">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-indigo-500" />
                    </div>
                    <div>
                      <h3 className="font-medium">Investors</h3>
                      <p className="text-sm text-muted-foreground">Import & manage investors</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/admin/documents" className="block">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-medium">Documents</h3>
                      <p className="text-sm text-muted-foreground">Track signing status</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Pending Action Alert */}
        {stats?.pending_review > 0 && (
          <Card className="border-orange-500/50 bg-orange-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                  <div>
                    <h3 className="font-medium text-orange-600 dark:text-orange-400">
                      {stats.pending_review} Deal{stats.pending_review > 1 ? 's' : ''} Pending Review
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Action required - review and moderate submitted deals
                    </p>
                  </div>
                </div>
                <Link to="/dashboard/admin/live-deals">
                  <Badge className="bg-orange-500 text-white cursor-pointer hover:bg-orange-600">
                    Review Now
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Badge>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
